/**
 * Multi-workspace type definitions
 * Aligns with backend Context Architecture v2
 */

// Workspace types: Report (primary), Graph (DAG audit)
export type WorkspaceType = 'report' | 'graph';

// Workspace MODE: controls which phase is active (sent to backend, controls tool sets + context)
// Distinct from WorkspaceType which controls sub-tabs within the research view
export type WorkspaceMode = 'plan' | 'institution_context' | 'research' | 'production' | 'documentation' | 'output_package';

// Sync status for workspace operations
export type SyncStatus = 'idle' | 'pending' | 'syncing' | 'synced' | 'error';

// Institution context item (shared at institution level)
export interface InstitutionContextItem {
  id: string;
  category: 'classification' | 'jurisdiction' | 'business_model' | 'regulatory_approach' | 'custom';
  label: string;
  value: string;
  description?: string;
  created_by?: string;
  created_at?: string;
}

// Workspace switch proposal from agent
export interface WorkspaceSwitchProposal {
  from_workspace: WorkspaceType;
  to_workspace: WorkspaceType;
  reason: string;
  key_findings?: string[];
  recommended_structure?: string[];
}

// Summary of a workspace for cross-workspace visibility
export interface WorkspaceSummary {
  type: WorkspaceType;
  status: 'active' | 'paused' | 'background';
  findingsCount: number;
  lastUpdated: string;
  briefDescription?: string;
}

// Handoff sync notification from live sync
export interface HandoffSyncNotification {
  handoff_id: string;
  summary: string;
  new_count: number;
  updated_count: number;
  removed_count: number;
  changes: Record<string, Array<{ id: string; change: string; summary: string }>>;
  timestamp: string;
}

// =============================================================================
// Plan workspace types (planning phase)
// =============================================================================

export type PlanItemStatus = 'planned' | 'active' | 'done' | 'revised';

export interface PlanItem {
  item_id: string;
  section_id: string;
  text: string;
  status: PlanItemStatus;
  revision_note?: string;
  phase_origin?: string;
  order: number;
}

export interface PlanSection {
  section_id: string;
  title: string;
  target_phase: string;
  order: number;
  items: PlanItem[];
}

export interface PlanState {
  objective: string;
  sections: PlanSection[];
  documents: Array<{
    document_id: string;
    filename: string;
    summary: string;
    status: string;
  }>;
  is_ready: boolean;
}

// =============================================================================
// Institution context workspace types (profiling phase)
// =============================================================================

export type ConfidenceLevel = 'confirmed' | 'inferred';

export interface ProfileField {
  field_id: string;
  label: string;
  value: string;
  confidence: ConfidenceLevel;
}

export interface RegulatoryChoice {
  choice_id: string;
  area: string;
  approach: string;
  scope_note?: string;
  confidence: ConfidenceLevel;
}

export interface KeyFigure {
  figure_id: string;
  metric: string;
  value: string;
  as_of_date?: string;
  source?: string;
}

export interface OpenItem {
  item_id: string;
  description: string;
  related_to?: string;
  status: 'open' | 'resolved';
  resolution?: string;
}

export interface InstitutionProfileState {
  profile_fields: ProfileField[];
  regulatory_choices: RegulatoryChoice[];
  key_figures: KeyFigure[];
  open_items: OpenItem[];
  is_ready: boolean;
}

export const DEFAULT_PLAN_STATE: PlanState = {
  objective: '',
  sections: [],
  documents: [],
  is_ready: false,
};

export const DEFAULT_INSTITUTION_PROFILE: InstitutionProfileState = {
  profile_fields: [],
  regulatory_choices: [],
  key_figures: [],
  open_items: [],
  is_ready: false,
};

// Production types — paused, kept for backwards compatibility with existing project data
export type ArtifactType = 'doc' | 'sheet' | 'notebook';

export interface ProductionArtifact {
  artifact_id: string;
  type: ArtifactType;
  url: string;
  title: string;
  document_id: string;
  handoff_ids?: string[];
  created_at: string;
  created_by: 'agent' | 'user';
}

export interface ProductionState {
  googleDocUrl: string | null;
  googleSheetUrl: string | null;
  jupyterNotebookUrl: string | null;
  isNotebookLocked: boolean;
  selectedFindingIds: string[];
  handoffSyncNotification: HandoffSyncNotification | null;
  artifacts: ProductionArtifact[];
  activeArtifactId: string | null;
}

// Multi-workspace state additions for projectStore
export interface MultiWorkspaceState {
  activeWorkspace: WorkspaceType;
  workspaceSyncStatus: Record<WorkspaceType, SyncStatus>;
  pendingWorkspaceSwitch: WorkspaceSwitchProposal | null;
  productionState: ProductionState;
}

// Institution context state (separate store)
export interface InstitutionContextState {
  items: InstitutionContextItem[];
  activeItemIds: string[];
  isLoading: boolean;
  error: string | null;
}

// Props for WorkspaceTabs component
export interface WorkspaceTabsProps {
  activeWorkspace: WorkspaceType;
  onWorkspaceChange: (workspace: WorkspaceType) => void;
  pendingSwitch?: WorkspaceSwitchProposal | null;
  onAcceptSwitch?: () => void;
  onRejectSwitch?: () => void;
}

// Props for InstitutionContextPanel
export interface InstitutionContextPanelProps {
  items: InstitutionContextItem[];
  activeItemIds: string[];
  onItemToggle: (itemId: string, isActive: boolean) => void;
  onItemAdd: (item: Omit<InstitutionContextItem, 'id'>) => void;
  onItemEdit: (itemId: string, updates: Partial<InstitutionContextItem>) => void;
  onItemDelete: (itemId: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}

// Default values
export const DEFAULT_PRODUCTION_STATE: ProductionState = {
  googleDocUrl: null,
  googleSheetUrl: null,
  jupyterNotebookUrl: null,
  isNotebookLocked: false,
  selectedFindingIds: [],
  handoffSyncNotification: null,
  artifacts: [],
  activeArtifactId: null,
};

export const DEFAULT_WORKSPACE_SYNC_STATUS: Record<WorkspaceType, SyncStatus> = {
  report: 'idle',
  graph: 'idle',
};
